import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import GeneralRevisionContent from './GeneralRevisionContent';
import GeneralRevisionModal from '../components/GeneralRevisionModal';

const form = {
  keyword: '',
  status: '',
};

const statusOptions = [
  { label: 'ALL STATUS', value: '' },
  { label: 'DRAFT', value: 'DRAFT' },
  { label: 'PUBLISHED', value: 'PUBLISHED' },
];

function GeneralRevisionContainer({
  match,
  history,
  getGeneralRevisionList,
  generalRevisionList,
  isLoading,
  removeGeneralRevision,
  generalRevisionListPager,
  getSingleGeneralRevision,
}) {
  const [formData, setFormData] = useState(form);
  const [filter, setFilter] = useState(form);
  const setModal = useContext(ModalContext);

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleOnChangeSelect = (key) => (e) => {
    setFilter({
      ...filter,
      [key]: e.value,
      status: e.value,
    });
    getGeneralRevisionList({
      ...filter,
      status: e.value,
    });
  };

  const handleAddModal = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <GeneralRevisionModal type="NEW" />,
      title: 'Add General Revision',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <GeneralRevisionModal
          type="EDIT"
          updateList={data.attributes}
          id={data.id}
        />
      ),
      title: 'Update General Revision',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove this General Revision?',
      () => {
        removeGeneralRevision(data.id, () => {
          getGeneralRevisionList();
          getGeneralRevisionList({}, (response) => {
            const id = _.get(response, 'data.0.id');
            history.push(`${id}`);
            getSingleGeneralRevision(id);
          });
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  useEffect(() => {
    getGeneralRevisionList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    q: formData.keyword,
    status: filter.status,
    sort_by: 'asc',
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      getGeneralRevisionList({ q: formData.keyword, status: filter.status });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm]);

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:generalRevisionId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="General Revision"
            getData={getGeneralRevisionList}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search..."
            pathName={`${match.path}/:generalRevisionId?`}
            listName="General Revisions"
            label="General Revisions"
            renderItem={(item) => (
              <div className="p-3">{_.get(item, 'attributes.name')}</div>
            )}
            isLoading={isLoading}
            statusFilter
            statusOptions={statusOptions}
            status={_.get(filter, 'status') || ''}
            setStatusFilter={handleOnChangeSelect('status')}
            selected={params.generalRevisionId}
            data={generalRevisionList}
            dataPager={generalRevisionListPager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:generalRevisionId?`}
                    render={() => (
                      <GeneralRevisionContent
                        handleAddModal={handleAddModal}
                        handleEdit={handleEdit}
                        handleRemove={handleRemove}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:generalRevisionId?`}
                  render={() =>
                    !params.generalRevisionId &&
                    generalRevisionList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(generalRevisionList, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

GeneralRevisionContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getGeneralRevisionList: PropTypes.instanceOf(Function).isRequired,
  generalRevisionList: PropTypes.instanceOf(Array).isRequired,
  generalRevisionListPager: PropTypes.instanceOf(Object).isRequired,
  removeGeneralRevision: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getSingleGeneralRevision: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  generalRevisionList: _.get(api, `${c.GET_GENERAL_REVISION}.list`) || [],
  generalRevisionListPager:
    _.get(api, `${c.GET_GENERAL_REVISION}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_GENERAL_REVISION) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(GeneralRevisionContainer);
